import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';

// Detect a Supabase recovery-link landing.
// After the user clicks the reset email, they arrive at /auth?mode=reset with a URL
// hash like #access_token=...&type=recovery. We must show the "set new password"
// form instead of auto-redirecting to /dashboard.
function isRecoveryLanding(params) {
  if (params.get('mode') === 'reset') return true;
  const hash = typeof window !== 'undefined' ? (window.location.hash || '') : '';
  return hash.includes('type=recovery');
}

export default function Auth() {
  const [params] = useSearchParams();
  const recovery = isRecoveryLanding(params);
  const initialMode = recovery ? 'reset' : (params.get('mode') || 'login');
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const { signIn, signUp, resetPassword, session } = useAuth();
  const nav = useNavigate();

  // Guarded auto-redirect: only redirect an already-logged-in user to /dashboard
  // when they are NOT in the middle of a password-reset flow.
  useEffect(() => {
    if (session && mode !== 'reset') nav('/dashboard');
  }, [session, mode, nav]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(''); setInfo(''); setBusy(true);
    try {
      if (mode === 'signup') {
        const { error } = await signUp(email, password);
        if (error) throw error;
        setInfo('Check your email to confirm your account, then log in.');
        setMode('login');
      } else if (mode === 'login') {
        const { error } = await signIn(email, password);
        if (error) throw error;
        nav('/dashboard');
      } else if (mode === 'forgot') {
        const { error } = await resetPassword(email);
        if (error) throw error;
        setInfo('If an account exists for that email, a reset link has been sent. Check your inbox (and spam folder).');
      } else if (mode === 'reset') {
        // Supabase already opened a temporary session via the email link's URL hash.
        // We just need to call updateUser({ password }) to finalize.
        if (!password || password.length < 6) throw new Error('Password must be at least 6 characters.');
        if (password !== confirmPassword) throw new Error('Passwords do not match.');
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
        // Clean session so the user has to log in fresh with the new password
        await supabase.auth.signOut();
        setInfo('Password updated. Please log in with your new password.');
        setMode('login');
        setPassword('');
        setConfirmPassword('');
        // Strip the recovery hash from the URL so a refresh does not re-trigger reset mode
        if (typeof window !== 'undefined' && window.history?.replaceState) {
          window.history.replaceState(null, '', '/auth?mode=login');
        }
      }
    } catch (e2) {
      const msg = e2.message || 'Something went wrong';
      // Friendlier hint for the Supabase email quota error the user already saw once
      if (/email rate limit/i.test(msg) || /over_email_send/i.test(msg)) {
        setErr('The email service is temporarily rate-limited. Please try again in a few minutes, or contact support.');
      } else {
        setErr(msg);
      }
    } finally { setBusy(false); }
  };

  const title = mode === 'signup' ? 'Sign Up'
    : mode === 'forgot' ? 'Recover'
    : mode === 'reset' ? 'Set New Password'
    : 'Log In';
  const subtitle = mode === 'signup' ? 'Join the defenders'
    : mode === 'forgot' ? 'Reset your password'
    : mode === 'reset' ? 'Choose the password you will use from now on'
    : 'Welcome back';
  const cta = busy ? 'Working...'
    : mode === 'signup' ? 'Create Account'
    : mode === 'forgot' ? 'Send Reset Link'
    : mode === 'reset' ? 'Update Password'
    : 'Log In';

  return (
    <div className="min-h-screen bg-plai-bg flex flex-col" data-testid="auth-page">
      <div className="px-6 py-5 border-b border-white/10">
        <Link to="/"><Logo size="sm" /></Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="glass iridescent-border p-8" data-testid="auth-card">
            <h1 className="font-headings text-4xl uppercase tracking-tight mb-2">{title}</h1>
            <p className="font-body text-xs tracking-[0.2em] uppercase text-white/50 mb-8">{subtitle}</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email input: hidden on reset (we already have a session from the recovery link) */}
              {mode !== 'reset' && (
                <div>
                  <label className="font-body text-[10px] tracking-[0.2em] uppercase text-white/60 block mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" strokeWidth={1.5} />
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-3 py-3 text-sm" placeholder="you@example.com"
                      data-testid="auth-email-input" />
                  </div>
                </div>
              )}

              {(mode === 'login' || mode === 'signup' || mode === 'reset') && (
                <div>
                  <label className="font-body text-[10px] tracking-[0.2em] uppercase text-white/60 block mb-2">
                    {mode === 'reset' ? 'New Password' : 'Password'}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" strokeWidth={1.5} />
                    <input type={showPwd ? 'text' : 'password'} required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-10 py-3 text-sm" placeholder="At least 6 characters"
                      data-testid="auth-password-input" />
                    <button type="button" onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                      data-testid="auth-toggle-pwd-btn">
                      {showPwd ? <EyeOff className="h-4 w-4" strokeWidth={1.5} /> : <Eye className="h-4 w-4" strokeWidth={1.5} />}
                    </button>
                  </div>
                </div>
              )}

              {mode === 'reset' && (
                <div>
                  <label className="font-body text-[10px] tracking-[0.2em] uppercase text-white/60 block mb-2">Confirm New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" strokeWidth={1.5} />
                    <input type={showPwd ? 'text' : 'password'} required minLength={6} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-10 pr-3 py-3 text-sm" placeholder="Retype your new password"
                      data-testid="auth-confirm-password-input" />
                  </div>
                </div>
              )}

              {err && <div className="font-body text-xs text-red-400 border border-red-500/30 bg-red-500/10 p-3" data-testid="auth-error">{err}</div>}
              {info && <div className="font-body text-xs text-plai-teal border border-plai-teal/30 bg-plai-teal/10 p-3" data-testid="auth-info">{info}</div>}

              <button type="submit" disabled={busy} className="btn-primary-cta w-full" data-testid="auth-submit-btn">
                {cta}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/10 flex flex-col gap-2 text-center">
              {mode === 'login' && (
                <>
                  <button onClick={() => setMode('signup')} className="font-body text-xs tracking-wide text-white/60 hover:text-white" data-testid="auth-go-signup">Need an account? Sign up</button>
                  <button onClick={() => setMode('forgot')} className="font-body text-xs tracking-wide text-white/40 hover:text-white" data-testid="auth-go-forgot">Forgot password?</button>
                </>
              )}
              {mode === 'signup' && (
                <button onClick={() => setMode('login')} className="font-body text-xs tracking-wide text-white/60 hover:text-white" data-testid="auth-go-login">Already have an account? Log in</button>
              )}
              {mode === 'forgot' && (
                <button onClick={() => setMode('login')} className="font-body text-xs tracking-wide text-white/60 hover:text-white" data-testid="auth-go-login-2">Back to log in</button>
              )}
              {mode === 'reset' && (
                <button onClick={() => setMode('login')} className="font-body text-xs tracking-wide text-white/60 hover:text-white" data-testid="auth-go-login-3">Back to log in</button>
              )}
            </div>
          </div>

          <p className="font-body text-[10px] tracking-[0.2em] uppercase text-white/30 text-center mt-6">
            By signing up you agree this is an informational tool and not legal advice.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
